import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import ModelMaster from "./ModelMaster";
import AdminUtilities from "./AdminUtilities";
import UserManagement from "./UserManagement";
import SalesforceAdmin from "./SalesforceAdmin";

const inputCls = "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0E2240]";

function Network() {
  const [branches, setBranches] = useState([]);
  const [zhs, setZhs] = useState([]);
  const [bName, setBName] = useState("");
  const [bCode, setBCode] = useState("");
  const [codeDrafts, setCodeDrafts] = useState({});
  const [check, setCheck] = useState(null);
  const load = () => {
    api.get("/branches").then((r) => setBranches(r.data));
    api.get("/users", { params: { role: "zh", status: "active" } }).then((r) => setZhs(r.data)).catch(() => {});
  };
  useEffect(load, []);
  const missing = branches.filter((b) => !b.dealer_code);
  const zhFor = (b) => zhs.find((z) => (z.branch_ids || []).includes(b.id));
  const addBranch = async () => {
    if (!bName.trim() || !bCode.trim()) { toast.error("Location name and dealer code are required"); return; }
    try { await api.post("/branches", { name: bName.trim(), dealer_code: bCode }); toast.success(`${bCode.toUpperCase()} — ${bName} created. Now create its SM/TDC/Security logins in Users and assign a Zonal Head.`); setBName(""); setBCode(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveCode = async (b) => {
    const code = (codeDrafts[b.id] || "").trim();
    if (!code) { toast.error("Enter a dealer code"); return; }
    try { await api.patch(`/branches/${b.id}`, { dealer_code: code }); toast.success(`${b.name} → ${code.toUpperCase()}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const openDelete = async (b) => {
    try { const { data } = await api.get(`/branches/${b.id}/delete-check`); setCheck(data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const confirmDelete = async () => {
    try { const { data } = await api.delete(`/branches/${check.branch.id}`); toast.success(`Location deleted · ${data.history_kept.trips} past trips kept on record`); setCheck(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const BLOCKER_LABELS = { active_vehicles: "Active vehicles", active_users: "Active users", open_bookings: "Open bookings", open_trips: "Trips in progress", open_transfers: "Open transfers" };
  return (
    <div className="mt-4 space-y-4">
      {missing.length > 0 && (
        <div data-testid="dealer-code-flag" className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-800">
          {missing.length} location{missing.length > 1 ? "s are" : " is"} missing a Dealer Code — the primary identifier for a location. Enter codes below (e.g. KL101).
        </div>
      )}
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-bold text-[#0E2240]">Add a dealer code / location</h3>
        <p className="mt-0.5 text-[11px] text-slate-500">Dealer codes are the only hierarchy. Zonal Heads are given a set of dealer codes in Users.</p>
        <div className="mt-2 flex gap-2 flex-wrap">
          <input data-testid="branch-code-input" value={bCode} onChange={(e) => setBCode(e.target.value.toUpperCase())} placeholder="Dealer code * e.g. KL107" className={inputCls + " w-36"} />
          <input data-testid="branch-name-input" value={bName} onChange={(e) => setBName(e.target.value)} placeholder="Location name *" className={inputCls + " flex-1 min-w-[140px]"} />
          <button data-testid="branch-add-btn" onClick={addBranch} className="mt-1 rounded-xl bg-[#BB162B] px-4 text-xs font-bold text-white">Add Location</button>
        </div>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h3 className="font-heading text-sm font-bold text-[#0E2240]">Dealer codes <span className="text-slate-400 font-normal">· {branches.length} locations</span></h3>
        <div className="mt-2 space-y-1.5">
          {branches.map((b) => {
            const zh = zhFor(b);
            return (
              <div key={b.id} data-testid={`branch-row-${b.id}`} className={`flex items-center justify-between gap-2 rounded-lg p-2.5 text-sm flex-wrap ${b.dealer_code ? "bg-slate-50" : "bg-amber-50 border border-amber-200"}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-700">{b.dealer_code ? `${b.dealer_code} — ${b.name}` : b.name}</div>
                  <div className="text-[10px]">
                    {zh ? <span className="text-slate-500">Zonal Head: {zh.name}</span>
                      : <span data-testid={`branch-no-zh-${b.id}`} className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-800">No Zonal Head assigned</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input data-testid={`dealer-code-input-${b.id}`} defaultValue={b.dealer_code || ""} placeholder={b.dealer_code ? "" : "Code e.g. KL101"}
                    onChange={(e) => setCodeDrafts({ ...codeDrafts, [b.id]: e.target.value.toUpperCase() })}
                    className="w-24 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] uppercase" />
                  <button data-testid={`dealer-code-save-${b.id}`} onClick={() => saveCode(b)}
                    className="rounded-lg bg-[#0E2240] px-2.5 py-1 text-[10px] font-bold text-white">Set</button>
                  <button data-testid={`branch-delete-${b.id}`} onClick={() => openDelete(b)}
                    className="rounded-lg border border-red-200 px-2.5 py-1 text-[10px] font-bold text-[#BB162B]">Delete</button>
                </div>
              </div>
            );
          })}
          {branches.length === 0 && <div className="text-xs text-slate-400">No locations yet — add the first dealer code above.</div>}
        </div>
      </div>
      {check && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" onClick={() => setCheck(null)}>
          <div data-testid="branch-delete-dialog" className="w-full max-w-md rounded-t-2xl sm:rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-[#0E2240]">Delete {check.branch.dealer_code} — {check.branch.name}?</h3>
            {check.can_delete ? (
              <p className="mt-2 text-xs text-emerald-800 bg-emerald-50 rounded-lg p-2.5">
                Nothing active remains here. Past history stays on record ({check.history.trips} trips · {check.history.bookings} bookings · {check.history.deactivated_users} deactivated users) and will show as "(deleted)" in reports.
              </p>
            ) : (
              <div className="mt-2 rounded-lg bg-red-50 p-2.5 text-xs text-[#BB162B]">
                <b>Cannot delete yet — clear these first:</b>
                <ul className="mt-1 list-disc pl-4">
                  {Object.entries(check.blockers).filter(([, n]) => n).map(([k, n]) => <li key={k} data-testid={`branch-blocker-${k}`}>{n} {BLOCKER_LABELS[k]}</li>)}
                </ul>
                <div className="mt-1.5 text-slate-600">Transfer vehicles out (or retire/sell them), move or deactivate/delete the users, and close open bookings, trips and transfers.</div>
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => setCheck(null)} className="rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-500">Cancel</button>
              <button data-testid="branch-delete-confirm-btn" disabled={!check.can_delete} onClick={confirmDelete}
                className="rounded-xl bg-[#BB162B] py-3 text-sm font-bold text-white disabled:opacity-40">Delete Location</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Config() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);
  const save = async () => {
    try { await api.put("/config", { scope: "global", ...cfg }); toast.success("Configuration saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!cfg) return null;
  const fields = [["block_minutes", "Default block window (min)"], ["home_block_hours", "Home TD window (hrs)"],
    ["auto_approve_km", "Auto-approve below (km)"], ["deviation_pct", "Deviation threshold (%)"],
    ["odo_tolerance_km", "Odometer tolerance (±km)"], ["ageing_months", "Ageing threshold (months)"], ["ageing_km", "Ageing threshold (km)"],
    ["disposal_alert_days", "Disposal ageing alert (days)"], ["additional_drive_cap", "Additional-model drives cap (per enquiry / 30d)"],
    ["advance_slot_minutes", "Advance block slot length (min)"], ["advance_max_days", "Advance block horizon (days)"]];
  return (
    <div className="mt-4 max-w-md space-y-3">
      {fields.map(([k, l]) => (
        <div key={k} className="flex items-center justify-between gap-3">
          <label className="text-sm text-slate-600">{l}</label>
          <input data-testid={`config-${k}`} type="number" value={cfg[k]} onChange={(e) => setCfg({ ...cfg, [k]: parseFloat(e.target.value) })}
            className="w-28 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-right" />
        </div>
      ))}
      {[["hard_block_noncompliant", "Hard-block non-compliant vehicles", "config-hard-block-toggle"],
        ["auto_approve_all", "Auto-approve all test drives (skip SM)", "config-auto-approve-all-toggle"],
        ["auto_approve_utility_fuel", "Auto-approve TDC fuel runs (utility)", "config-auto-approve-fuel-toggle"],
        ["tdc_can_add_vehicles", "TDC can add vehicles (own branch)", "config-tdc-add-toggle"],
        ["bulk_import_enabled", "Bulk vehicle import enabled", "config-bulk-import-toggle"]].map(([k, l, tid]) => (
        <div key={k} className="flex items-center justify-between gap-3">
          <label className="text-sm text-slate-600">{l}</label>
          <button data-testid={tid} onClick={() => {
            if (k === "bulk_import_enabled" && !window.confirm(cfg[k] ? "Disable bulk import? Admins will only be able to add/edit vehicles directly in Vehicle Master." : "Re-enable bulk import for the next onboarding batch?")) return;
            setCfg({ ...cfg, [k]: !cfg[k] });
          }}
            className={`rounded-full px-4 py-1.5 text-xs font-bold ${cfg[k] ? "bg-[#BB162B] text-white" : "bg-slate-200 text-slate-600"}`}>
            {cfg[k] ? "ON" : "OFF"}
          </button>
        </div>
      ))}
      <button data-testid="config-save-btn" onClick={save} className="w-full rounded-xl bg-[#0E2240] py-3 text-sm font-bold text-white">Save Configuration</button>
    </div>
  );
}

function Users() {
  return <UserManagement />;
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState(user.role === "sm" ? "users" : user.role === "admin" ? "network" : "config");
  const [banner, setBanner] = useState(null);
  const isAdmin = user.role === "admin";
  useEffect(() => { if (isAdmin || user.role === "vp") api.get("/admin/clear-banner").then((r) => setBanner(r.data)).catch(() => {}); }, [isAdmin, user.role]);
  const tabs = user.role === "sm" ? [["users", "KEC Team"]]
    : isAdmin ? [["network", "Network"], ["models", "Models"], ["config", "Configuration"], ["users", "Users"], ["salesforce", "Salesforce"], ["utilities", "Utilities"]]
      : [["config", "Configuration"], ["users", "Users"]];
  return (
    <div className="px-4 py-5 lg:px-8 max-w-3xl">
      <h1 className="font-heading text-2xl font-bold text-[#0E2240]">Administration</h1>
      {banner?.show && (
        <div data-testid="clear-data-banner" className="mt-3 rounded-xl border border-red-300 bg-red-50 p-3 text-xs font-bold text-[#BB162B]">
          Data cleared by {banner.by} on {new Date(banner.date).toLocaleDateString("en-IN")} · scope: {banner.clear_scope}
        </div>
      )}
      <div className="mt-4 flex rounded-xl bg-slate-100 p-1 overflow-x-auto">
        {tabs.map(([t, l]) => (
          <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
            className={`flex-1 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-bold ${tab === t ? "bg-[#0E2240] text-white" : "text-slate-500"}`}>{l}</button>
        ))}
      </div>
      {tab === "network" && <Network />}
      {tab === "models" && <ModelMaster />}
      {tab === "config" && <Config />}
      {tab === "users" && <Users />}
      {tab === "salesforce" && <SalesforceAdmin />}
      {tab === "utilities" && <AdminUtilities />}
    </div>
  );
}
